import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

export type ModeAcces = 'connexion' | 'inscription'

const OPTIONS: { mode: ModeAcces; libelle: string }[] = [
  { mode: 'connexion', libelle: 'Se connecter' },
  { mode: 'inscription', libelle: 'Créer un compte' },
]

/** Durée de la sortie du contenu avant l'entrée du suivant (fondu enchaîné). */
const DUREE_SORTIE_MS = 140

export const idPanneauAcces = 'panneau-acces'
export const idOngletAcces = (m: ModeAcces) => `onglet-acces-${m}`

/**
 * Commutateur à deux états « Se connecter | Créer un compte ».
 *
 * Une pastille glisse sous l'option active (transform CSS, voir `.bascule` dans
 * la feuille de style). Accessibilité : `tablist` / `tab`, flèches gauche/droite
 * (et Début/Fin) pour changer d'option, un seul onglet dans l'ordre de tabulation.
 */
export function BasculeAcces({
  mode,
  surChangement,
  desactive = false,
}: {
  mode: ModeAcces
  surChangement: (m: ModeAcces) => void
  desactive?: boolean
}) {
  const refs = useRef<Record<ModeAcces, HTMLButtonElement | null>>({ connexion: null, inscription: null })

  function auClavier(e: KeyboardEvent<HTMLButtonElement>) {
    let cible: ModeAcces | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') cible = mode === 'connexion' ? 'inscription' : 'connexion'
    else if (e.key === 'Home') cible = 'connexion'
    else if (e.key === 'End') cible = 'inscription'
    if (!cible) return
    e.preventDefault()
    if (desactive) return
    surChangement(cible)
    refs.current[cible]?.focus()
  }

  return (
    <div className="bascule" role="tablist" aria-label="Accès à votre compte" data-mode={mode}>
      <span className="bascule-pastille" aria-hidden="true" />
      {OPTIONS.map((o) => {
        const actif = o.mode === mode
        return (
          <button
            key={o.mode}
            ref={(el) => {
              refs.current[o.mode] = el
            }}
            type="button"
            role="tab"
            id={idOngletAcces(o.mode)}
            aria-selected={actif}
            aria-controls={idPanneauAcces}
            tabIndex={actif ? 0 : -1}
            disabled={desactive}
            className="bascule-option"
            onClick={() => surChangement(o.mode)}
            onKeyDown={auClavier}
          >
            {o.libelle}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Fondu enchaîné : le commutateur change tout de suite, le contenu sort (fondu
 * de 140 ms) puis le nouveau entre. `contenu` est le mode actuellement AFFICHÉ.
 */
export function useContenuAcces(mode: ModeAcces): { contenu: ModeAcces; sortant: boolean } {
  const [contenu, setContenu] = useState<ModeAcces>(mode)
  const [sortant, setSortant] = useState(false)

  useEffect(() => {
    if (mode === contenu) {
      setSortant(false)
      return
    }
    setSortant(true)
    const reduit = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const t = window.setTimeout(
      () => {
        setContenu(mode)
        setSortant(false)
      },
      reduit ? 0 : DUREE_SORTIE_MS,
    )
    return () => window.clearTimeout(t)
  }, [mode, contenu])

  return { contenu, sortant }
}
